/**
 * EventDetailsModal — details view for calendar events (#351).
 *
 * Opens when the studio taps an event on the calendar. This is the
 * PRIMARY tap target — see the event's full info first (hero image,
 * time, location, description, big tap-to-join meeting button). Edit
 * is a secondary action so the "I'm running to a 3pm call, join me
 * in one tap" case never gets in the way.
 *
 * Source badge (Orbit / Google / Microsoft / Apple / Booking) sits
 * next to the title so once we're pulling in external calendars, the
 * studio always knows where the event came from — matters for
 * "why did this appear on my Orbit calendar?" moments.
 */

import { format } from 'date-fns';
import { X, MapPin, Video, Pencil, Trash2, ExternalLink, Copy, Briefcase } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export interface DetailedCalendarEvent {
  id: string;
  title: string;
  subtitle: string;
  /** Event start time. */
  startAt: Date;
  /** Event end time. Falls back to start + 60min at the caller. */
  endAt: Date;
  /** All-day flag — when true we render "All day" instead of a time range. */
  allDay?: boolean;
  location?: string;
  description?: string;
  meetingUrl?: string;
  imageUrl?: string;
  projectId?: string;
  /** Where the event originated — controls the source badge. */
  source: 'orbit' | 'google' | 'microsoft' | 'apple' | 'booking';
  /** Type from the underlying data model — controls delete eligibility. */
  type: 'personal' | 'due_date' | 'milestone' | 'booking';
}

interface Props {
  event: DetailedCalendarEvent | null;
  onClose: () => void;
  onEdit?: (event: DetailedCalendarEvent) => void;
  onDelete?: (event: DetailedCalendarEvent) => void;
}

const SOURCE_STYLES: Record<DetailedCalendarEvent['source'], { label: string; bg: string; fg: string }> = {
  orbit:     { label: 'Orbit',     bg: 'rgba(212,84,31,0.15)',  fg: '#F4A882' },
  google:    { label: 'Google',    bg: 'rgba(66,133,244,0.18)', fg: '#82B4FA' },
  microsoft: { label: 'Microsoft', bg: 'rgba(0,120,212,0.18)',  fg: '#6FB5EF' },
  apple:     { label: 'iCloud',    bg: 'rgba(255,255,255,0.10)', fg: '#EDEDED' },
  booking:   { label: 'Booking',   bg: 'rgba(52,211,153,0.15)',  fg: '#6EE7B7' },
};

export default function EventDetailsModal({ event, onClose, onEdit, onDelete }: Props) {
  const navigate = useNavigate();
  if (!event) return null;

  const src = SOURCE_STYLES[event.source];

  const timeLabel = event.allDay
    ? 'All day'
    : `${format(event.startAt, 'h:mm a')} – ${format(event.endAt, 'h:mm a')}`;

  const dayLabel = format(event.startAt, 'EEEE, MMMM d');

  const copyLocation = () => {
    if (!event.location) return;
    navigator.clipboard.writeText(event.location).then(
      () => toast.success('Location copied'),
      () => toast.error("Couldn't copy"),
    );
  };

  const openProject = () => {
    if (!event.projectId) return;
    navigate(`/studio/${event.projectId}`);
    onClose();
  };

  const openMeeting = () => {
    if (!event.meetingUrl) return;
    // Guard: only allow well-formed URLs; anything else (raw phone
    // numbers, dial-in strings) opens as a text tel: fallback.
    try {
      const u = new URL(event.meetingUrl);
      window.open(u.href, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(event.meetingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-[#1a1a1c] border border-white/[0.10] rounded-t-3xl md:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero image (only when present) — full-bleed at the top so
            events with header art land with real presence. */}
        {event.imageUrl && (
          <div className="w-full h-56 md:h-64 overflow-hidden bg-white/[0.04]">
            <img
              src={event.imageUrl}
              alt=""
              className="w-full h-full object-cover object-center"
              loading="lazy"
            />
          </div>
        )}

        <div className="p-6 space-y-5">
          {/* Header row: source badge + close */}
          <div className="flex items-start justify-between gap-3">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.14em]"
              style={{ backgroundColor: src.bg, color: src.fg }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: src.fg }} />
              {src.label}
            </span>
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/55 hover:text-white hover:bg-white/[0.08]"
            >
              <OrbitIcon name="xmark" fallback={X} className="w-4 h-4" />
            </button>
          </div>

          {/* Title + subtitle */}
          <div>
            <h2 className="font-editorial text-3xl md:text-4xl text-white leading-none">
              {event.title}
            </h2>
            {event.subtitle && (
              <p className="text-sm text-white/55 mt-1">{event.subtitle}</p>
            )}
          </div>

          {/* Time — prominent */}
          <div className="space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">When</p>
            <p className="text-base text-white">{dayLabel}</p>
            <p className="text-sm text-white/75 tabular-nums">{timeLabel}</p>
          </div>

          {/* Meeting link — big tap-to-join button. Sits ABOVE
              location because for video calls this is the action
              99% of studios are here for. */}
          {event.meetingUrl && (
            <button
              onClick={openMeeting}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[#275FBE] hover:bg-[#1E4C99] text-white font-semibold text-sm transition-colors"
            >
              <OrbitIcon name="video.fill" fallback={Video} className="w-4 h-4" />
              Join meeting
              <OrbitIcon name="arrow.up.right.square" fallback={ExternalLink} className="w-3.5 h-3.5 opacity-60" />
            </button>
          )}

          {/* Location */}
          {event.location && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">Where</p>
              <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <OrbitIcon name="mappin.and.ellipse" fallback={MapPin} className="w-4 h-4 shrink-0 text-white/55" />
                  <p className="text-sm text-white truncate">{event.location}</p>
                </div>
                <button
                  onClick={copyLocation}
                  aria-label="Copy address"
                  className="text-white/45 hover:text-white p-1 rounded-md hover:bg-white/[0.08]"
                >
                  <OrbitIcon name="doc.on.doc" fallback={Copy} className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">Notes</p>
              <p className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                {event.description}
              </p>
            </div>
          )}

          {/* Project chip */}
          {event.projectId && (
            <button
              onClick={openProject}
              className="w-full inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07] transition-colors text-left"
            >
              <span className="inline-flex items-center gap-2 text-sm text-white">
                <OrbitIcon name="briefcase" fallback={Briefcase} className="w-4 h-4 text-white/55" />
                Open project workspace
              </span>
              <OrbitIcon name="arrow.up.right.square" fallback={ExternalLink} className="w-4 h-4 text-white/45" />
            </button>
          )}

          {/* Footer actions — Edit + Delete only for events the
              studio owns (Orbit-authored, not external syncs). */}
          <div className="flex items-center gap-2 pt-2">
            {onEdit && event.source === 'orbit' && event.type === 'personal' && (
              <button
                onClick={() => onEdit(event)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white/75 hover:text-white hover:bg-white/[0.06]"
              >
                <OrbitIcon name="pencil" fallback={Pencil} className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
            {onDelete && event.source === 'orbit' && event.type === 'personal' && (
              <button
                onClick={() => onDelete(event)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-red-300 hover:text-red-200 hover:bg-red-500/10"
              >
                <OrbitIcon name="trash" fallback={Trash2} className="w-3.5 h-3.5" />
                Delete
              </button>
            )}
            <div className="ml-auto">
              <button
                onClick={onClose}
                className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.1em] text-white/75 hover:text-white hover:bg-white/[0.06]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
